import {
  createGrid,
  GridOptions,
  ColDef,
  CellClassParams,
  CellValueChangedEvent,
  ProcessDataFromClipboardParams,
  UndoEndedEvent,
  IRowNode,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../ag_grid_base_component/ag_grid_base_controller";
import { ebEditableTheme } from "../ag_grid_shared/eb_theme";
import { ebFilterParams, ebLocaleText } from "../ag_grid_shared/filter_defaults";
import { NO_CLIENT_EXPORT } from "../ag_grid_shared/no_client_export";
import { showConfirmModal } from "../ag_grid_shared/confirm_modal";

// Plain flat rows keyed by column id. Unlike AgGridEditableComponent (Type A),
// there's no per-cell read_only metadata: every cell here is editable.
type GrowableRow = Record<string, string>;

interface ValidateRowError {
  row: number;
  // Integer positions into this row's fixed column order (Vote::MultipleDuplicate::Input#col_error),
  // NOT colId strings.
  cols: number[];
  errors: string[];
}

interface ValidateResponse {
  election_errors?: ValidateRowError[];
}

// Shape of the final-submit-failure highlight event the parent Multiple Duplicate
// controller dispatches. Already flattened to individual {row, col} pairs, NOT the
// same {row, cols, errors} shape ValidateRowError uses.
interface HighlightMultipleCellsDetail {
  cells: { row: number; col: number; message?: string }[];
}

// Shape of the single-to-multiple mode toggle's "prefill row 1" event — keyed by
// colId, matching growable_column_defs order.
interface FillFirstRowDetail {
  firstRow: Record<string, string>;
}

// Shape of the `row-validation-errors` event this controller dispatches on `document`
// so the parent Multiple Duplicate controller can surface background validation
// errors in its persistent errorMessageTarget banner.
interface RowValidationErrorsDetail {
  errors: { row: number; message: string }[];
}

const ERROR_CELL_CLASS = "ag-grid-growable-cell--error";
const VALIDATION_DEBOUNCE_MS = 600;

/**
 * Growable, bulk-paste AG-Grid controller ("Type C"). Starts with a small number
 * of blank rows and grows toward maxRowsValue as the user pastes/types (inverse of
 * AgGridEditableComponent's clampPasteToGrid, which clamps paste to a fixed roster).
 *
 * Gather-for-submit: the consuming form supplies its own hidden field via the
 * `output_field` slot, tagged with `data-<controller-id>-target="output"`. This
 * controller keeps that field's value in sync with the current non-blank rows on
 * every change. Consumers that don't use the slot can instead call the public
 * `gatherRows()` method directly (returns every row, including blank ones).
 */
export default class extends AgGridBaseController {
  static targets = [...AgGridBaseController.targets, "output"];

  static values = {
    ...AgGridBaseController.values,
    validateUrl: String,
    initialBlankRows: Number,
    maxRows: Number,
  };

  declare readonly validateUrlValue: string;
  declare readonly initialBlankRowsValue: number;
  declare readonly maxRowsValue: number;

  declare readonly outputTarget: HTMLInputElement;
  declare readonly hasOutputTarget: boolean;

  // Keyed by row index; full replacement on every validation response.
  private errorMap: Map<number, Set<string>> = new Map();

  // Parallel to errorMap: the row's human-readable validation message(s), dispatched
  // to the parent's persistent error banner (see dispatchRowErrors).
  private errorMessages: Map<number, string> = new Map();

  private validationTimer: ReturnType<typeof setTimeout> | null = null;

  // Bumped on every runValidation() call so a slow, superseded response can't clobber
  // a later, already-applied one (e.g. an edit fired mid-flight of an earlier request).
  private validationRequestId = 0;

  // Entirely-blank rows are dropped before every outbound payload, which shifts row
  // positions — this maps each sent payload index back to its real AG-Grid rowIndex.
  private lastValidationRowIndexMap: number[] = [];

  // Same row-position problem as lastValidationRowIndexMap, but for the
  // final-submit-failure response, which indexes into the output field's row set.
  private lastOutputRowIndexMap: number[] = [];

  // Row nodes the most recent paste added, so undo can also remove them (handleUndoEnded).
  private lastPasteGrowthNodeIds: string[] | null = null;

  connect() {
    super.connect(); // license key + outside click handler

    if (!this.hasGridTarget) return;

    const options: GridOptions = {
      theme: ebEditableTheme,
      localeText: ebLocaleText,
      columnDefs: this.buildGrowableColumnDefs(this.columnDefsValue),
      rowData: this.buildBlankRows(this.initialBlankRowsValue),
      // "Growable" means the box itself grows with the row count, not that a
      // fixed-size viewport scrolls internally — autoHeight sizes the grid to
      // exactly header + rows.
      domLayout: "autoHeight",
      suppressMovableColumns: true,
      suppressDragLeaveHidesColumns: true,
      suppressRowDrag: true,
      rowGroupPanelShow: "never",
      cellSelection: true,
      // 50 is AG-Grid's minimum render width for this column; lower values are ignored.
      rowNumbers: { width: 50, minWidth: 50 },
      stopEditingWhenCellsLoseFocus: true,
      undoRedoCellEditing: true,
      processDataFromClipboard: (params: ProcessDataFromClipboardParams) => this.growGridForPaste(params),
      onCellValueChanged: (event: CellValueChangedEvent) => this.handleCellValueChanged(event),
      onFirstDataRendered: () => this.autoSizeNonFlexColumns(),
      onUndoEnded: (event: UndoEndedEvent) => this.handleUndoEnded(event),
      // Menu popups render outside the grid's own (short/narrow) bounding box instead
      // of being clipped by it.
      popupParent: document.body,
      getMainMenuItems: (params) => this.resetColumnsOnlyMainMenuItems(params),
      defaultColDef: {
        editable: true,
        sortable: false,
        resizable: true,
        suppressMovable: true,
        // Right-click still opens the (now Reset-Columns-only) menu; only the
        // always-visible 3-dot button is removed.
        suppressHeaderMenuButton: true,
        filterParams: ebFilterParams,
      },
      ...this.gridOptionsValue,
      ...NO_CLIENT_EXPORT,
    };

    this.gridApi = createGrid(this.gridTarget, options);

    this.gridBlurHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (this.gridTarget.contains(target)) return;
      this.gridApi?.clearCellSelection();
    };
    document.addEventListener("mousedown", this.gridBlurHandler);

    this.writeOutput();
  }

  disconnect() {
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
      this.validationTimer = null;
    }
    super.disconnect();
  }

  // ── Abstract overrides — this grid has no basic/advanced toggle ──────────

  switchToBasic(): void {
    // no-op: view_toggle is never enabled for this component
  }

  switchToAdvanced(): void {
    // no-op: view_toggle is never enabled for this component
  }

  // ── Gather-for-submit ──────────────────────────────────────────────────────

  // Public: consumers that don't use the `output_field` slot can call this directly
  // (e.g. via Stimulus outlets or `application.getControllerForElementAndIdentifier`).
  gatherRows(): GrowableRow[] {
    const rows: GrowableRow[] = [];
    // forEachNode ignores filtering/sorting (ag-grid-community gridApi.d.ts) — this
    // grid has neither enabled, so load order is display order either way.
    this.gridApi?.forEachNode((node) => {
      if (node.data) rows.push(node.data as GrowableRow);
    });
    return rows;
  }

  private writeOutput(): void {
    const { rows, rowIndexMap } = this.gatherNonBlankRows();
    this.lastOutputRowIndexMap = rowIndexMap;
    if (!this.hasOutputTarget) return;
    this.outputTarget.value = JSON.stringify(rows);
  }

  // Computed on every cell commit so the hidden output field is always submit-ready.
  private gatherNonBlankRows(): { rows: GrowableRow[]; rowIndexMap: number[] } {
    const rows: GrowableRow[] = [];
    const rowIndexMap: number[] = [];
    this.gridApi?.forEachNode((node) => {
      if (!node.data || node.rowIndex === null) return;
      const row = node.data as GrowableRow;
      if (!this.rowHasContent(row)) return;
      rows.push(row);
      rowIndexMap.push(node.rowIndex);
    });
    return { rows, rowIndexMap };
  }

  // ── Column defs / error highlighting ──────────────────────────────────────

  private buildGrowableColumnDefs(columnDefs: ColDef[]): ColDef[] {
    return columnDefs.map((colDef) => {
      const colId = this.resolveColId(colDef);
      return {
        ...colDef,
        cellClassRules: {
          [ERROR_CELL_CLASS]: (params: CellClassParams) => this.isCellInError(params.node.rowIndex, colId),
        },
      };
    });
  }

  private isCellInError(rowIndex: number | null, colId: string | undefined): boolean {
    if (rowIndex === null || !colId) return false;
    const cols = this.errorMap.get(rowIndex);
    return !!cols && cols.has(colId);
  }

  private resolveColId(colDef: ColDef): string | undefined {
    return colDef.colId ?? (typeof colDef.field === "string" ? colDef.field.split(".")[0] : undefined);
  }

  private getColKeys(columnDefs: ColDef[]): string[] {
    return columnDefs
      .map((colDef) => this.resolveColId(colDef))
      .filter((colId): colId is string => Boolean(colId));
  }

  private buildBlankRows(count: number): GrowableRow[] {
    const colKeys = this.getColKeys(this.columnDefsValue);
    return Array.from({ length: Math.max(count, 0) }, () => {
      const row: GrowableRow = {};
      colKeys.forEach((key) => {
        row[key] = "";
      });
      return row;
    });
  }

  // ── Growable paste ─────────────────────────────────────────────────────────
  // Grows the grid to fit the paste, up to maxRowsValue, before AG-Grid's own
  // paste-fill continues (inverse of clampPasteToGrid, which clamps to a fixed roster).
  private growGridForPaste(params: ProcessDataFromClipboardParams): string[][] | null {
    if (!this.gridApi) return params.data;
    const focusedCell = this.gridApi.getFocusedCell();
    if (!focusedCell) return params.data;

    this.lastPasteGrowthNodeIds = null;

    const totalRows = this.gridApi.getDisplayedRowCount();
    const rowsNeeded = focusedCell.rowIndex + params.data.length - totalRows;
    if (rowsNeeded <= 0) return params.data;

    const capacityRemaining = this.maxRowsValue - totalRows;
    const rowsToAdd = Math.min(rowsNeeded, capacityRemaining);
    if (rowsToAdd > 0) {
      const result = this.gridApi.applyTransaction({ add: this.buildBlankRows(rowsToAdd) });
      this.lastPasteGrowthNodeIds = (result?.add ?? [])
        .map((node) => node.id)
        .filter((id): id is string => id !== undefined);
    }

    if (rowsNeeded <= capacityRemaining) return params.data;

    // Truncate rather than silently drop the overflow — surface it to the user.
    alert(`Pasted data exceeds the ${this.maxRowsValue}-row limit. Rows beyond the limit were not added.`);
    const allowedRows = this.maxRowsValue - focusedCell.rowIndex;
    return allowedRows > 0 ? params.data.slice(0, allowedRows) : null;
  }

  // AG-Grid's undo only reverts cell values, not row structure — best-effort undo
  // the most recent paste's growth too, if those rows are still blank afterward.
  private handleUndoEnded(event: UndoEndedEvent): void {
    if (!event.operationPerformed) return;

    const nodeIds = this.lastPasteGrowthNodeIds;
    this.lastPasteGrowthNodeIds = null;
    if (!nodeIds || !this.gridApi) return;

    const nodes = nodeIds
      .map((id) => this.gridApi!.getRowNode(id))
      .filter((node): node is IRowNode<GrowableRow> => !!node);

    if (nodes.length !== nodeIds.length) return;
    if (nodes.some((node) => !node.data || this.rowHasContent(node.data))) return;

    // No getRowId is configured, so removal must match by data-object reference, not IRowNode.
    this.gridApi.applyTransaction({ remove: nodes.map((node) => node.data!) });
    this.writeOutput();
  }

  // ── Auto-append blank row ───────────────────────────────────────────────────

  private handleCellValueChanged(event: CellValueChangedEvent): void {
    this.notifyDirty();
    this.appendBlankRowIfLastRowFilled(event);
    this.writeOutput();
    this.scheduleValidation();
    // Re-fit columns whose content grew past the initial autosize on first render.
    this.autoSizeNonFlexColumns();
  }

  // Feeds the parent's existing beforeunload/isDirty guard by dispatching the
  // same `handsontable-changed` bubbling event the legacy path already uses.
  private notifyDirty(): void {
    this.element.dispatchEvent(new CustomEvent('handsontable-changed', { bubbles: true }));
  }

  private appendBlankRowIfLastRowFilled(event: CellValueChangedEvent): void {
    if (!this.gridApi) return;
    const totalRows = this.gridApi.getDisplayedRowCount();
    if (event.rowIndex === null || event.rowIndex !== totalRows - 1) return;
    if (totalRows >= this.maxRowsValue) return;
    if (!this.rowHasContent(event.data as GrowableRow)) return;

    this.gridApi.applyTransaction({ add: this.buildBlankRows(1) });
  }

  private rowHasContent(row: GrowableRow): boolean {
    return Object.values(row).some((value) => (value ?? "").toString().trim() !== "");
  }

  // ── Debounced background validation ─────────────────────────────────────────
  // Every commit resets the timer, so a burst of edits produces one validation POST.

  private scheduleValidation(): void {
    if (this.validationTimer) clearTimeout(this.validationTimer);
    this.validationTimer = setTimeout(() => this.runValidation(), VALIDATION_DEBOUNCE_MS);
  }

  private runValidation(): void {
    if (!this.validateUrlValue) return;

    // Build the request from the surrounding <form> so it can never drift from
    // whatever the real submit sends, then swap in the live (non-blank) rows.
    const form = this.element.closest("form") as HTMLFormElement | null;
    if (!form) return;

    const { rows, rowIndexMap } = this.gatherNonBlankRows();
    this.lastValidationRowIndexMap = rowIndexMap;

    const body = new FormData(form);
    body.set("election_info", JSON.stringify(rows));

    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";

    // A field edited moments after a slower earlier request was sent must win — otherwise
    // that stale response can land afterward and reinstate already-fixed errors.
    const requestId = ++this.validationRequestId;

    fetch(this.validateUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
      body,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Validate request failed: ${response.status}`);
        return response.json() as Promise<ValidateResponse>;
      })
      .then((data) => {
        if (requestId !== this.validationRequestId) return;
        this.applyErrorMap(data);
      })
      .catch(() => {
        // A failed request just means stale/missing highlighting; the caller's own
        // submit flow still re-validates the full payload server-side before committing.
      });
  }

  // The parent dispatches one event per erroring row — merge each into the existing
  // error map rather than replacing it wholesale.
  handleHighlightMultipleCells(event: CustomEvent<HighlightMultipleCellsDetail>): void {
    const cells = event.detail?.cells ?? [];
    if (cells.length === 0) return;

    const colKeys = this.getColKeys(this.columnDefsValue);

    cells.forEach(({ row, col, message }) => {
      // `row` is a position in the filtered (non-blank) payload just submitted, not
      // the live grid's rowIndex — translate back the same way applyErrorMap does.
      const gridRowIndex = this.lastOutputRowIndexMap[row];
      if (gridRowIndex === undefined) return;

      const colId = colKeys[col];
      if (!colId) return;

      const existing = this.errorMap.get(gridRowIndex) ?? new Set<string>();
      existing.add(colId);
      this.errorMap.set(gridRowIndex, existing);

      // Every cell dispatched for a given row carries the same joined message (see
      // handleError), so just setting it here is idempotent across repeat calls.
      if (message) this.errorMessages.set(gridRowIndex, message);
    });

    this.gridApi?.refreshCells({ force: true });
  }

  // Writes straight into row 0's live RowNode (setDataValue triggers AG-Grid's own
  // cell refresh), then resyncs the hidden output field.
  handleFillFirstRow(event: CustomEvent<FillFirstRowDetail>): void {
    if (!this.gridApi) return;

    const firstRow = event.detail?.firstRow;
    if (!firstRow) return;

    const firstNode = this.gridApi.getDisplayedRowAtIndex(0);
    if (!firstNode) return;

    this.getColKeys(this.columnDefsValue).forEach((colId) => {
      if (firstRow[colId] !== undefined) firstNode.setDataValue(colId, firstRow[colId]);
    });

    this.writeOutput();
  }

  // ── Add Rows / Clear Rows buttons ─────────────────────────────────────────

  // Adds up to 40 blank rows, capped at maxRowsValue; no-op once already at the cap.
  handleAddRows(): void {
    if (!this.gridApi) return;

    const totalRows = this.gridApi.getDisplayedRowCount();
    const remaining = this.maxRowsValue - totalRows;
    if (remaining <= 0) return;

    this.gridApi.applyTransaction({ add: this.buildBlankRows(Math.min(40, remaining)) });
    this.writeOutput();
  }

  // Dispatched by the parent Multiple Duplicate controller when a shared, non-grid
  // field (e.g. Voting Time start/end date) changes — reuses the same debounced
  // pipeline a cell commit would trigger, since those fields never fire onCellValueChanged.
  handleRevalidateRows(): void {
    this.scheduleValidation();
  }

  // Confirms first if any row has content, then resets to initialBlankRowsValue blanks.
  async handleClearRows(): Promise<void> {
    if (!this.gridApi) return;

    const { rows } = this.gatherNonBlankRows();
    if (rows.length > 0) {
      const confirmed = await showConfirmModal({ message: 'Are you sure you want to clear all rows?' });
      if (!confirmed) return;
    }

    this.gridApi.setGridOption('rowData', this.buildBlankRows(this.initialBlankRowsValue));
    this.errorMap = new Map();
    this.errorMessages = new Map();
    this.lastPasteGrowthNodeIds = null;
    this.gridApi.refreshCells({ force: true });
    this.writeOutput();
    this.dispatchRowErrors();
  }

  private applyErrorMap(data: ValidateResponse): void {
    // Backend `cols` are integer positions in this row's column order, not colId strings.
    const colKeys = this.getColKeys(this.columnDefsValue);

    this.errorMap = new Map();
    this.errorMessages = new Map();
    (data.election_errors ?? []).forEach((error) => {
      // error.row is a position in the filtered (non-blank) payload just sent, not
      // the live grid's rowIndex — translate back via the map captured at send time.
      const gridRowIndex = this.lastValidationRowIndexMap[error.row];
      if (gridRowIndex === undefined) return;

      const cols = new Set(
        error.cols.map((index) => colKeys[index]).filter((colId): colId is string => Boolean(colId))
      );
      this.errorMap.set(gridRowIndex, cols);

      if (error.errors.length > 0) this.errorMessages.set(gridRowIndex, error.errors.join(" || "));
    });

    this.gridApi?.refreshCells({ force: true });
    this.dispatchRowErrors();
  }

  // Surfaces errors in the parent's persistent banner, since cell highlighting alone
  // requires the user to notice and hover a red cell to learn why it's red.
  private dispatchRowErrors(): void {
    const errors = Array.from(this.errorMessages.entries())
      .sort(([rowA], [rowB]) => rowA - rowB)
      .map(([row, message]) => ({ row, message }));

    document.dispatchEvent(
      new CustomEvent<RowValidationErrorsDetail>("row-validation-errors", { detail: { errors } })
    );
  }
}
